import clsx from 'clsx';
import React, { forwardRef, memo } from 'react';
import styles from './button.module.css';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { CMSLink } from '~/shared/components';

type CommonButtonProps = {
    variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary';
    size?: 'small' | 'medium' | 'large';
    isDarkBackground?: boolean;
    withIcon?: boolean;
    description?: string;
    hoverEffect?: boolean;
    rounded?: boolean;
    centeredText?: boolean;
};

type AnchorProps = CommonButtonProps &
    React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
type ButtonProps = CommonButtonProps &
    React.ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };

type Props = AnchorProps | ButtonProps;

// TODO: Missing disabled state handling
const ButtonComponent = forwardRef<HTMLElement, Props>((props, ref) => {
    const {
        className,
        children,
        variant = 'primary',
        description,
        size = 'medium',
        href,
        hoverEffect,
        rounded,
        centeredText,
        ...restProps
    } = props;

    const sharedClassNames = clsx(
        styles.root,
        styles[`${size}Size`],
        styles[`${variant}Variant`],
        {
            [styles.hoverEffectOverlay]:
                hoverEffect && (variant === 'primary' || variant === 'secondary'),
            [styles.hoverEffectUnderline]: hoverEffect && variant === 'tertiary',
            [styles.rounded]: rounded && (variant === 'primary' || variant === 'secondary'),
        },
        { [styles.centeredText]: centeredText },
        className
    );

    const sharedProps = {
        'data-with-icon': props.withIcon ? 'true' : 'false',
        'data-dark-background': props.isDarkBackground ? 'true' : 'false',
    };

    const content = (
        <>
            {description && <VisuallyHidden>{description}</VisuallyHidden>}
            {children}
        </>
    );

    if (href) {
        return (
            <CMSLink
                ref={ref as React.Ref<HTMLAnchorElement>}
                className={sharedClassNames}
                href={href}
                {...sharedProps}
                {...(restProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
            >
                {content}
            </CMSLink>
        );
    }

    return (
        <button
            ref={ref as React.Ref<HTMLButtonElement>}
            className={sharedClassNames}
            {...sharedProps}
            {...(restProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
            {content}
        </button>
    );
});

const Button = memo(ButtonComponent);
Button.displayName = 'Button';

export { Button };
